import { compareTokensBy } from '@swarm/core/shared/utils/filters/compare-tokens-by'
import { unifyAddress } from '@swarm/core/web3'
import InfiniteTable from '@swarm/ui/presentational/InfiniteTable'
import AlertPanel from '@swarm/ui/swarm/AlertPanel'
import AssetFilter, { getAssetFilter } from '@swarm/ui/swarm/AssetFilter'
import map from 'lodash/map'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, Card, Heading, Text } from 'rimble-ui'
import styled from 'styled-components/macro'
import { useQueryParam } from 'use-query-params'

import { StakingCreationContextProvider } from '../StakeSecurityContext'
import { useStakingContext } from '../StakingContext'
import { AssetParam } from '../consts'

import RwaRow from './RwaRow'
import StakingSummary from './RwaSummary'

const StyledInfiniteTable = styled(InfiniteTable)`
  col {
    width: 10%;
  }

  colgroup.asset {
    col {
      &:last-child {
        min-width: 220px;
      }
    }
  }

  th,
  td {
    text-align: center;
    min-width: 90px;
    white-space: nowrap;

    &:first-child {
      width: 120px;
    }

    &:last-child {
      width: 150px;
    }

    &.asset {
      text-align: left;
    }
  }

  td {
    color: ${(props) => props.theme.colors['near-black']};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints?.[2]}) {
    col {
      width: 0%;
    }

    th,
    td {
      &:nth-child(1),
      &:nth-child(3),
      &:nth-child(7) {
        width: 0px;
        display: none;
      }

      &:nth-child(2) {
        width: 200px;
      }

      &:nth-child(4) {
        text-align: center;
        width: 70px;
      }

      &:nth-child(5) {
        width: 150px;
      }

      &:nth-child(6) {
        width: 70px;
      }

      &:last-child {
        width: 200px;
      }
    }
  }
`

const RwaTab = () => {
  const { t } = useTranslation('staking')

  const { securities, securitiesRewards, securitiesLoading, rewardsLoading } =
    useStakingContext()

  const [assetsParam, setAssetsParam] = useQueryParam('assets', AssetParam)

  const sortedSecurities = useMemo(() => {
    return securities.sort(compareTokensBy(['marketCap', 'desc']))
  }, [securities])

  return (
    <Box
      width="100%"
      bg="background"
      display="flex"
      flexDirection="column"
      p={[3, 3, 4]}
      flexGrow={1}
    >
      <AlertPanel page="staking" promptPassport={false} />
      <Card
        width="100%"
        p={4}
        borderRadius={1}
        overflow="hidden"
        display="flex"
        flexDirection="column"
        justifyContent="stretch"
      >
        <Box pb={3}>
          <StakingSummary securities={securities} />
        </Box>
        <Heading
          as="h3"
          mt={0}
          display="flex"
          justifyContent="space-between"
          alignItems="flex-start"
          flexWrap="wrap"
        >
          <Text.span
            color="grey"
            fontSize={3}
            lineHeight="28px"
            fontWeight={5}
            flexGrow={1}
            flexShrink={0}
            mb={3}
          >
            {t('headings.rwaLeaderboard')}
          </Text.span>
          <AssetFilter
            value={getAssetFilter(securities, assetsParam)}
            onSubmit={(value) => setAssetsParam(map(value, 'id'))}
            flexGrow={0}
            flexShrink={1}
            flexBasis="auto"
            tokens={securities}
            loading={securitiesLoading}
          />
        </Heading>
        <StakingCreationContextProvider>
          <StyledInfiniteTable
            colgroup={
              <>
                <colgroup className="asset">
                  <col />
                  <col />
                </colgroup>
                <colgroup className="summary">
                  <col />
                  <col />
                  <col />
                  <col />
                  <col />
                </colgroup>
                <colgroup className="actions">
                  <col />
                </colgroup>
              </>
            }
            head={
              <tr>
                <th>{t(`th.address`)}</th>
                <th className="asset" scope="colgroup">
                  {t(`th.asset`)}
                </th>
                <th>{t(`th.marketCap`)}</th>
                <th>{t(`th.vol24`)}</th>
                <th>{t(`th.totalStaked`)}</th>
                <th>{t(`th.apy`)}</th>
                <th>{t(`th.myTotalStaked`)}</th>
                <th> </th>
              </tr>
            }
            loading={securitiesLoading || rewardsLoading}
            hasMore={false}
            loadMore={() => {}}
            noResults={
              <tr>
                <td colSpan={7}>
                  <Text.p color="grey" textAlign="center" width="100%">
                    {t('noSecurities')}
                  </Text.p>
                </td>
              </tr>
            }
          >
            {sortedSecurities.map((security) => (
              <RwaRow
                key={security.id}
                security={security}
                smtReward={securitiesRewards[unifyAddress(security.id)]}
              />
            ))}
          </StyledInfiniteTable>
        </StakingCreationContextProvider>
      </Card>
    </Box>
  )
}

export default RwaTab
